import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { widgetByKey } from "@/lib/widgets";

const API = process.env.REACT_APP_BACKEND_URL;

function useWidgetScript(w, slug, site, mount) {
  useEffect(() => {
    if (!w || w.iframe || !mount.current) return;
    mount.current.innerHTML = w.markup || "";
    const s = document.createElement("script");
    s.async = true;
    if (w.product === "morrconnect") {
      s.src = `${API}/connect.js`;
      s.setAttribute("data-business", slug);
      Object.entries(w.attrs || {}).forEach(([k, v]) => s.setAttribute(k, v));
    } else {
      s.id = "morrcart-embed";
      s.src = `${API}/embed.js`;
      s.setAttribute("data-tenant", slug);
      s.setAttribute("data-morrcart-context", site ? "external" : "hosted");
    }
    document.body.appendChild(s);
    let timer;
    if (w.autoOpen) {
      let tries = 0;
      timer = setInterval(() => {
        const open = document.querySelector(".mc-drawer.open");
        if (window.MorrCart?.openAccount) window.MorrCart.openAccount(w.autoOpen);
        if (open || ++tries > 15) clearInterval(timer);
      }, 700);
    }
    return () => {
      clearInterval(timer);
      s.remove();
      document.querySelectorAll("[data-morrcart], script[src*='embed.full.js']").forEach((n) => n.remove());
      window.__morrcartLoaded = window.__morrcartLoaderRan = window.__morrconnect = undefined;
      window.MorrCart = undefined;
    };
  }, [w, slug, site, mount]);
}

function SiteChrome({ children, label }) {
  return (
    <div style={{ minHeight: "100vh", background: "#f7f4ee", color: "#23312a", fontFamily: "Georgia, 'Times New Roman', serif" }} data-testid="widget-sample-site">
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "22px 6vw", borderBottom: "1px solid #e3ddd1" }}>
        <strong style={{ fontSize: 22 }}>Sample Business Website</strong>
        <nav style={{ display: "flex", gap: 20, fontSize: 15 }}><span>Home</span><span>About</span><span>Contact</span></nav>
      </header>
      <p style={{ margin: "16px 6vw", fontSize: 13, background: "#fff", border: "1px dashed #cfc6b5", padding: "10px 14px", fontFamily: "system-ui, sans-serif" }}>
        This is a sample website. The {label} below is the widget, placed the way an owner would add it to their own site.
      </p>
      <section style={{ padding: "24px 6vw 60px" }}>{children}</section>
      <footer style={{ padding: "24px 6vw", borderTop: "1px solid #e3ddd1", fontSize: 13 }}>© Sample Business</footer>
    </div>
  );
}

// /w/:slug/:widget — one widget alone. ?site=1 shows it on a sample website.
export default function WidgetHost() {
  const { slug, widget } = useParams();
  const [params] = useSearchParams();
  const site = params.get("site") === "1";
  const w = widgetByKey(widget);
  const mount = useRef(null);
  const [off, setOff] = useState(false);
  useEffect(() => {
    fetch(`${API}/api/public/tenant/${slug}`).then((r) => r.json()).then((d) => setOff(d?.widgets?.[widget] === false)).catch(() => {});
  }, [slug, widget]);
  useWidgetScript(off ? null : w, slug, site, mount);
  useEffect(() => { if (w) document.title = `${w.label} · ${slug}`; }, [w, slug]);

  if (!w) return <div style={{ padding: 40, fontFamily: "system-ui" }} data-testid="widget-not-found">Unknown widget.</div>;
  if (off) return <div style={{ padding: 40, fontFamily: "system-ui", textAlign: "center" }} data-testid="widget-unavailable">This page isn't available.</div>;
  const body = w.iframe
    ? <iframe src={`${API}${w.iframe.replace("{slug}", slug)}`} title={w.label} style={{ width: "100%", minHeight: 900, border: 0 }} data-testid="widget-iframe" />
    : <div ref={mount} data-testid="widget-mount" style={{ minHeight: 200 }} />;
  if (site) return <SiteChrome label={w.label.toLowerCase()}>{body}</SiteChrome>;
  return (
    <div style={{ minHeight: "100vh", background: "#faf8f5", padding: "40px 24px", fontFamily: "system-ui, sans-serif" }} data-testid="widget-hosted">
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>{body}</div>
    </div>
  );
}
